import { Capacitor } from '@capacitor/core';
import { SQLiteDBConnection } from '@capacitor-community/sqlite';
import { captureException } from '@sentry/react';

import { sqliteConnector } from './sqlite-connector';

export abstract class SqliteDatabase {
  private dbConnectionPromise: Promise<SQLiteDBConnection> | undefined;
  private initTablesPromise: Promise<void> | undefined;

  constructor(
    private name: string,
    private version: number,
  ) {}

  protected async connect() {
    if (!this.dbConnectionPromise) {
      this.dbConnectionPromise = this.resolveConnection().catch((e) => {
        console.error(e);

        captureException(e, {
          level: 'error',
          extra: { message: `Failed to initialize database ${this.name}` },
        });

        throw e;
      });
    }

    return this.dbConnectionPromise!;
  }

  protected async persistChanges() {
    if (Capacitor.isNativePlatform()) {
      return;
    }

    const connection = await sqliteConnector.connect();
    await connection.saveToStore(this.name);
  }

  private async resolveConnection() {
    const connection = await sqliteConnector.connect();

    const consistency = await connection.checkConnectionsConsistency();
    const dbConnectionExists = await connection.isConnection(this.name, false);

    let dbConnection: SQLiteDBConnection;

    if (consistency.result && dbConnectionExists.result) {
      console.log(
        `Connection ${this.name} already exists, retrieving existing connection`,
      );

      dbConnection = await connection.retrieveConnection(this.name, false);
    } else {
      console.log(
        `Connection ${this.name} does not exist, creating new connection`,
      );
      dbConnection = await connection.createConnection(
        this.name,
        false,
        'no-encryption',
        this.version,
        false,
      );
    }

    await dbConnection.open();

    if (!this.initTablesPromise) {
      this.initTablesPromise = this.initTables(dbConnection);
    }

    await this.initTablesPromise;

    return dbConnection;
  }

  private async initTables(dbConnection: SQLiteDBConnection) {
    await this.onInitTables(dbConnection);
    await this.persistChanges();
  }

  protected abstract onInitTables(
    dbConnection: SQLiteDBConnection,
  ): Promise<void>;
}
